import type { ReportPayload } from '@finvox/report-engine';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@/components/ui/Pagination';
import { useFormatMoney } from '@/hooks/useFormatMoney';

type ReportViewProps = {
  data: ReportPayload;
  loading?: boolean;
  onGlPageChange?: (page: number) => void;
};

export function ReportView({ data, loading, onGlPageChange }: ReportViewProps) {
  const { t, i18n } = useTranslation();
  const fmt = useFormatMoney();
  const isAr = i18n.language.startsWith('ar');

  if (data.reportType === 'trial_balance') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[var(--fv-bg)]">
          <tr>
            <th className="p-2 text-start">{t('reports.code')}</th>
            <th className="p-2 text-start">{t('reports.name')}</th>
            <th className="p-2 text-end">{t('coa.debit')}</th>
            <th className="p-2 text-end">{t('coa.credit')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.accountId} className="border-t border-[var(--fv-border)]">
              <td className="p-2">{r.code}</td>
              <td className="p-2">{isAr && r.nameAr ? r.nameAr : r.name}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.debitBalance)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.creditBalance)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-semibold">
            <td colSpan={2} className="p-2">
              {t('reports.total')}
              {!data.balanced && (
                <span className="ms-2 text-red-600">({t('reports.unbalanced')})</span>
              )}
            </td>
            <td className="p-2 text-end tabular-nums">{fmt(data.totalDebit)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.totalCredit)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'profit_loss') {
    const sections = [data.revenue, data.cogs, data.expenses];
    return (
      <div className="space-y-6 text-sm">
        {sections.map((s) => (
          <div key={s.label}>
            <h3 className="font-semibold">{s.label}</h3>
            <table className="mt-2 w-full">
              <tbody>
                {s.rows.map((r) => (
                  <tr key={r.code} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{r.code}</td>
                    <td className="p-2">{r.name}</td>
                    <td className="p-2 text-end tabular-nums">{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr className="font-medium">
                  <td colSpan={2} className="p-2">
                    {t('reports.subtotal')}
                  </td>
                  <td className="p-2 text-end tabular-nums">{fmt(s.subtotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
        <p className="font-semibold">
          {t('reports.grossProfit')}: {fmt(data.grossProfit)}
        </p>
        <p className="font-semibold">
          {t('reports.netIncome')}: {fmt(data.netIncome)}
        </p>
        <p className="text-[var(--fv-text-muted)]">
          {t('reports.ytdNet')}: {fmt(data.ytdNetIncome)}
        </p>
      </div>
    );
  }

  if (data.reportType === 'balance_sheet') {
    const sections = [data.assets, data.liabilities, data.equity];
    return (
      <div className="space-y-6 text-sm">
        {sections.map((s) => (
          <div key={s.label}>
            <h3 className="font-semibold">{s.label}</h3>
            <table className="mt-2 w-full">
              <tbody>
                {s.rows.map((r) => (
                  <tr key={r.code} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{r.code}</td>
                    <td className="p-2">{r.name}</td>
                    <td className="p-2 text-end tabular-nums">{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr className="font-medium">
                  <td colSpan={2} className="p-2">
                    {t('reports.subtotal')}
                  </td>
                  <td className="p-2 text-end tabular-nums">{fmt(s.subtotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
        <p className="font-semibold">
          {t('reports.totalAssets')}: {fmt(data.totalAssets)} — {t('reports.totalLiabEq')}:{' '}
          {fmt(data.totalLiabilitiesEquity)}
          {!data.balanced && (
            <span className="ms-2 text-red-600">({t('reports.unbalanced')})</span>
          )}
        </p>
      </div>
    );
  }

  if (data.reportType === 'cash_flow') {
    return (
      <div className="space-y-2 text-sm">
        <p>
          {t('reports.netIncome')}: <span className="tabular-nums">{fmt(data.netIncome)}</span>
        </p>
        <p>
          {t('reports.wcChange')}:{' '}
          <span className="tabular-nums">{fmt(data.workingCapitalChange)}</span>
        </p>
        <p className="font-semibold">
          {t('reports.netCashOps')}:{' '}
          <span className="tabular-nums">{fmt(data.netCashFromOperations)}</span>
        </p>
        <p className="mt-4 text-xs text-[var(--fv-text-muted)]">{data.footerNote}</p>
      </div>
    );
  }

  if (data.reportType === 'equity_statement') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[var(--fv-bg)]">
          <tr>
            <th className="p-2 text-start">{t('reports.code')}</th>
            <th className="p-2 text-start">{t('reports.name')}</th>
            <th className="p-2 text-end">{t('reports.opening')}</th>
            <th className="p-2 text-end">{t('reports.movement')}</th>
            <th className="p-2 text-end">{t('reports.closing')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code} className="border-t border-[var(--fv-border)]">
              <td className="p-2">{r.code}</td>
              <td className="p-2">{r.name}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.opening)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.movement)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.closing)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 font-semibold">
            <td colSpan={2} className="p-2">
              {t('reports.summary')}
            </td>
            <td className="p-2 text-end tabular-nums">{fmt(data.openingEquity)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.netIncome)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.closingEquity)}</td>
          </tr>
        </tfoot>
      </table>
    );
  }

  if (data.reportType === 'journal_register') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[var(--fv-bg)]">
          <tr>
            <th className="p-2 text-start">{t('journals.date')}</th>
            <th className="p-2 text-start">{t('reports.source')}</th>
            <th className="p-2 text-start">{t('journals.memo')}</th>
            <th className="p-2 text-end">{t('coa.debit')}</th>
            <th className="p-2 text-end">{t('coa.credit')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="border-t border-[var(--fv-border)]">
              <td className="p-2">{r.date}</td>
              <td className="p-2">{r.source}</td>
              <td className="p-2">{r.memo ?? '—'}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.debitTotal)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.creditTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'general_ledger') {
    return (
      <div className="space-y-8 text-sm">
        {data.accounts.map((acc) => (
          <div key={acc.accountId}>
            <h3 className="font-semibold">
              {acc.code} — {acc.name}
            </h3>
            <p className="text-xs text-[var(--fv-text-muted)]">
              {t('reports.opening')}: {fmt(acc.openingBalance)}
            </p>
            <table className="mt-2 w-full">
              <thead>
                <tr className="text-[var(--fv-text-muted)]">
                  <th className="p-1 text-start">{t('journals.date')}</th>
                  <th className="p-1 text-end">{t('coa.debit')}</th>
                  <th className="p-1 text-end">{t('coa.credit')}</th>
                  <th className="p-1 text-end">{t('reports.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {acc.lines.map((l, i) => (
                  <tr key={`${l.entryId}-${i}`} className="border-t border-[var(--fv-border)]">
                    <td className="p-1">{l.date}</td>
                    <td className="p-1 text-end tabular-nums">{fmt(l.debit)}</td>
                    <td className="p-1 text-end tabular-nums">{fmt(l.credit)}</td>
                    <td className="p-1 text-end tabular-nums">{fmt(l.runningBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        <Pagination
          pageIndex={data.page - 1}
          hasNext={data.page * data.pageSize < data.totalAccounts}
          hasPrev={data.page > 1}
          onNext={() => onGlPageChange?.(data.page + 1)}
          onPrev={() => onGlPageChange?.(data.page - 1)}
          loading={loading}
        />
        <p className="text-xs text-[var(--fv-text-muted)]">
          {t('reports.pageInfo', { page: data.page, total: data.totalAccounts })}
        </p>
      </div>
    );
  }

  if (data.reportType.startsWith('vat_summary_')) {
    const vat = data as Extract<ReportPayload, { reportType: `vat_summary_${string}` }>;
    return (
      <div className="space-y-4 text-sm">
        <h3 className="text-lg font-semibold">{isAr ? vat.titleAr : vat.title}</h3>
        <p>
          {vat.startDate} — {vat.endDate}
        </p>
        <table className="w-full">
          <tbody>
            <tr className="border-t">
              <td className="p-2">{t('reports.vatOutput')}</td>
              <td className="p-2 text-end tabular-nums">{fmt(vat.outputVat)}</td>
            </tr>
            <tr className="border-t">
              <td className="p-2">{t('reports.vatInput')}</td>
              <td className="p-2 text-end tabular-nums">{fmt(vat.inputVat)}</td>
            </tr>
            <tr className="border-t font-semibold">
              <td className="p-2">{t('reports.vatNet')}</td>
              <td className="p-2 text-end tabular-nums">{fmt(vat.netPayable)}</td>
            </tr>
          </tbody>
        </table>
        <p className="text-xs text-[var(--fv-text-muted)]">{vat.footerNote}</p>
      </div>
    );
  }

  if (data.reportType === 'profit_loss_by_cost_center') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2 text-start">{t('settings.costCenterName')}</th>
            <th className="p-2 text-end">{t('reports.plCcRevenue')}</th>
            <th className="p-2 text-end">{t('reports.plCcExpenses')}</th>
            <th className="p-2 text-end">{t('reports.plCcNet')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.costCenterId} className="border-t">
              <td className="p-2">{r.costCenterName}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.revenue)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.expenses)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'fixed_asset_schedule') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('fixedAssets.name')}</th>
            <th className="p-2 text-end">{t('fixedAssets.cost')}</th>
            <th className="p-2 text-end">{t('fixedAssets.nbv')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2">{r.name}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.cost)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.netBookValue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'payroll_summary') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('journals.date')}</th>
            <th className="p-2 text-end">{t('payroll.netPay')}</th>
            <th className="p-2">{t('journals.status')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2">{r.payDate}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.netPay)}</td>
              <td className="p-2">{r.status}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 text-sm font-medium">
          {t('reports.total')}: {fmt(data.totalNet)}
        </p>
      </table>
    );
  }

  if (data.reportType === 'e_invoice_register') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('sales.invoice')}</th>
            <th className="p-2">{t('journals.date')}</th>
            <th className="p-2">UUID</th>
            <th className="p-2 text-end">{t('sales.total')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="p-2">{r.documentNumber}</td>
              <td className="p-2">{r.date}</td>
              <td className="p-2 font-mono text-xs">{r.eInvoiceUuid}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.grossTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'bank_reconciliation') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('reports.code')}</th>
            <th className="p-2">{t('reports.name')}</th>
            <th className="p-2 text-end">{t('reports.glBalance')}</th>
            <th className="p-2 text-end">{t('reports.unreconciled')}</th>
          </tr>
        </thead>
        <tbody>
          {data.accounts.map((a) => (
            <tr key={a.code} className="border-t">
              <td className="p-2">{a.code}</td>
              <td className="p-2">{a.name}</td>
              <td className="p-2 text-end tabular-nums">{fmt(a.glBalance)}</td>
              <td className="p-2 text-end tabular-nums">
                {a.unreconciledCount} ({fmt(a.unreconciledTotal)})
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'inventory_valuation') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('reports.code')}</th>
            <th className="p-2">{t('reports.name')}</th>
            <th className="p-2 text-end">{t('inventory.qty')}</th>
            <th className="p-2 text-end">{t('inventory.unitCost')}</th>
            <th className="p-2 text-end">{t('reports.total')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code} className="border-t">
              <td className="p-2">{r.code}</td>
              <td className="p-2">{r.name}</td>
              <td className="p-2 text-end tabular-nums">{r.qty}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.unitCost)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.value)}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 font-medium">
          {t('reports.total')}: {fmt(data.totalValue)}
        </p>
      </table>
    );
  }

  if (data.reportType === 'customer_statement' || data.reportType === 'vendor_statement') {
    return (
      <div className="text-sm">
        <p className="font-medium">{data.partyName}</p>
        <table className="mt-2 w-full">
          <thead>
            <tr>
              <th className="p-2">{t('journals.date')}</th>
              <th className="p-2">{t('reports.reference')}</th>
              <th className="p-2 text-end">{t('coa.debit')}</th>
              <th className="p-2 text-end">{t('coa.credit')}</th>
              <th className="p-2 text-end">{t('reports.balance')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">{r.date}</td>
                <td className="p-2">{r.reference}</td>
                <td className="p-2 text-end tabular-nums">{fmt(r.debit)}</td>
                <td className="p-2 text-end tabular-nums">{fmt(r.credit)}</td>
                <td className="p-2 text-end tabular-nums">{fmt(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 font-medium">
          {t('reports.closingBalance')}: {fmt(data.closingBalance)}
        </p>
      </div>
    );
  }

  if (data.reportType === 'audit_export') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('reports.at')}</th>
            <th className="p-2">{t('settings.auditLog')}</th>
            <th className="p-2">User</th>
            <th className="p-2">{t('journals.memo')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2 font-mono text-xs">{r.at.slice(0, 19)}</td>
              <td className="p-2">{r.action}</td>
              <td className="p-2">{r.userId}</td>
              <td className="p-2 text-xs">{r.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.reportType === 'withholding_summary') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('journals.date')}</th>
            <th className="p-2">{t('reports.reference')}</th>
            <th className="p-2">{t('purchases.vendor')}</th>
            <th className="p-2 text-end">{t('purchases.withholding')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2">{r.date}</td>
              <td className="p-2">{r.reference}</td>
              <td className="p-2">{r.vendorName}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.amount)}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 font-medium">
          {t('reports.total')}: {fmt(data.totalWithheld)}
        </p>
      </table>
    );
  }

  if (data.reportType === 'tax_reconciliation') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('reports.metric')}</th>
            <th className="p-2 text-end">{t('reports.subledger')}</th>
            <th className="p-2 text-end">{t('reports.gl')}</th>
            <th className="p-2 text-end">{t('reports.variance')}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t">
            <td className="p-2">{t('reports.vatOutput')}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.outputVatSubledger)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.outputVatGl)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.outputVariance)}</td>
          </tr>
          <tr className="border-t">
            <td className="p-2">{t('reports.vatInput')}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.inputVatSubledger)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.inputVatGl)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.inputVariance)}</td>
          </tr>
          <tr className="border-t font-medium">
            <td className="p-2">{t('reports.vatNet')}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.netPayableSubledger)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.netPayableGl)}</td>
            <td className="p-2 text-end tabular-nums">{fmt(data.netVariance)}</td>
          </tr>
        </tbody>
        <p className="mt-2 text-xs text-[var(--fv-text-muted)]">{data.footerNote}</p>
      </table>
    );
  }

  if (data.reportType === 'budget_vs_actual') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('reports.code')}</th>
            <th className="p-2">{t('reports.name')}</th>
            <th className="p-2 text-end">{t('settings.budgetAmount')}</th>
            <th className="p-2 text-end">{t('reports.actual')}</th>
            <th className="p-2 text-end">{t('reports.variance')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code} className="border-t">
              <td className="p-2">{r.code}</td>
              <td className="p-2">{r.name}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.budget)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.actual)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.variance)}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 font-medium">
          {t('reports.total')}: {fmt(data.totalBudget)} / {fmt(data.totalActual)} ({fmt(data.totalVariance)})
        </p>
      </table>
    );
  }

  if (data.reportType === 'depreciation_report') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('journals.date')}</th>
            <th className="p-2">ID</th>
            <th className="p-2">{t('journals.memo')}</th>
            <th className="p-2 text-end">{t('reports.total')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.entryId} className="border-t">
              <td className="p-2">{r.date}</td>
              <td className="p-2 font-mono text-xs">{r.entryId}</td>
              <td className="p-2">{r.memo}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.amount)}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 font-medium">
          {t('reports.total')}: {fmt(data.totalDepreciation)}
        </p>
      </table>
    );
  }

  if (data.reportType === 'sales_register') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('sales.invoice')}</th>
            <th className="p-2">{t('journals.date')}</th>
            <th className="p-2">{t('sales.customer')}</th>
            <th className="p-2 text-end">{t('sales.total')}</th>
            <th className="p-2 text-end">{t('reports.balance')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="p-2">{r.documentNumber}</td>
              <td className="p-2">{r.date}</td>
              <td className="p-2">{r.customerName}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.grossTotal)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.balanceDue)}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 text-sm font-medium">
          {t('reports.total')}: {fmt(data.totalGross)} ({t('reports.totalPaid')}: {fmt(data.totalPaid)})
        </p>
      </table>
    );
  }

  if (data.reportType === 'purchase_register') {
    return (
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('purchases.bill')}</th>
            <th className="p-2">{t('journals.date')}</th>
            <th className="p-2">{t('purchases.vendor')}</th>
            <th className="p-2 text-end">{t('sales.total')}</th>
            <th className="p-2 text-end">{t('purchases.withholding')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="p-2">{r.documentNumber}</td>
              <td className="p-2">{r.date}</td>
              <td className="p-2">{r.vendorName}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.grossTotal)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.withholdingTotal)}</td>
            </tr>
          ))}
        </tbody>
        <p className="mt-2 text-sm font-medium">
          {t('reports.total')}: {fmt(data.totalGross)}
        </p>
      </table>
    );
  }

  if (data.reportType === 'ar_aging' || data.reportType === 'ap_aging') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[var(--fv-bg)]">
          <tr>
            <th className="p-2 text-start">{t('sales.customer')}</th>
            <th className="p-2 text-end">{t('reports.agingCurrent')}</th>
            <th className="p-2 text-end">30</th>
            <th className="p-2 text-end">60</th>
            <th className="p-2 text-end">90</th>
            <th className="p-2 text-end">90+</th>
            <th className="p-2 text-end">{t('reports.total')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.partyId} className="border-t border-[var(--fv-border)]">
              <td className="p-2">{r.partyName}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.current)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.days30)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.days60)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.days90)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.over90)}</td>
              <td className="p-2 text-end tabular-nums">{fmt(r.total)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-semibold">
            <td className="p-2">{t('reports.total')}</td>
            <td colSpan={6} className="p-2 text-end tabular-nums">
              {fmt(data.grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return null;
}
